import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext.jsx';
import Navbar from './components/Navbar.jsx';
import LoginPage from './pages/LoginPage.jsx';

import RegisterPage from './pages/RegisterPage.jsx';
import DashboardPage from './pages/DashboardPage.jsx';
import EmployeesPage from './pages/EmployeesPage.jsx';
import AvailabilityPage from './pages/AvailabilityPage.jsx';
import RosterPage from './pages/RosterPage.jsx';
import SettingsPage from './pages/SettingsPage.jsx';
import OpenShiftsPage from './pages/OpenShiftsPage.jsx';
import AccountPage from './pages/AccountPage.jsx';
import ForgotPasswordPage from './pages/ForgotPasswordPage.jsx';
import ResetPasswordPage from './pages/ResetPasswordPage.jsx';
import AuditLogPage from './pages/AuditLogPage.jsx';
import HelpPage from './pages/HelpPage.jsx';

// Darken/lighten a hex color by `amount` (negative = darker)
function adjustHexBrightness(hex, amount) {
  const h = hex.replace('#', '');
  const num = parseInt(h, 16);
  let r = Math.min(255, Math.max(0, (num >> 16) + amount));
  let g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amount));
  let b = Math.min(255, Math.max(0, (num & 0xff) + amount));
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

// Create a very light tint of the color for backgrounds
function hexToLight(hex) {
  const h = hex.replace('#', '');
  const num = parseInt(h, 16);
  const r = (num >> 16);
  const g = ((num >> 8) & 0xff);
  const b = (num & 0xff);
  // Mix 15% color + 85% white
  const lr = Math.round(r * 0.15 + 255 * 0.85);
  const lg = Math.round(g * 0.15 + 255 * 0.85);
  const lb = Math.round(b * 0.15 + 255 * 0.85);
  return '#' + [lr, lg, lb].map(v => v.toString(16).padStart(2, '0')).join('');
}

function ProtectedRoute({ children, ownerOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) return <div className="page-loading">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (ownerOnly && user.role !== 'owner') return <Navigate to="/dashboard" replace />;

  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="page-loading">Loading...</div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

const AUTH_PATHS = ['/login', '/register', '/forgot-password', '/reset-password'];

export default function App() {
  const { user } = useAuth();
  const location = useLocation();

  // Apply restaurant brand color as CSS variables
  React.useEffect(() => {
    const color = user?.brand_color || '#2563eb';
    document.documentElement.style.setProperty('--color-primary', color);
    document.documentElement.style.setProperty('--color-primary-dark', adjustHexBrightness(color, -30));
    document.documentElement.style.setProperty('--color-primary-light', hexToLight(color));
  }, [user?.brand_color]);
  const isAuthPage = AUTH_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '?'));
  const mainClass = user ? 'main-with-nav' : isAuthPage ? 'main-full' : 'main-public';

  return (
    <div className="app" data-role={user?.role || 'guest'}>
      {user && <Navbar />}
      <main className={mainClass}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/staff/login" element={<Navigate to="/login?tab=staff" replace />} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Protected routes */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/employees" element={<ProtectedRoute ownerOnly><EmployeesPage /></ProtectedRoute>} />
          <Route path="/availability" element={<ProtectedRoute><AvailabilityPage /></ProtectedRoute>} />
          <Route path="/roster" element={<ProtectedRoute><RosterPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute ownerOnly><SettingsPage /></ProtectedRoute>} />
          <Route path="/open-shifts" element={<ProtectedRoute><OpenShiftsPage /></ProtectedRoute>} />
          <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
          <Route path="/audit" element={<ProtectedRoute ownerOnly><AuditLogPage /></ProtectedRoute>} />
          <Route path="/help" element={<HelpPage />} />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
}
